import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";
import { 
  Radio, Activity, Copy, Check, 
  User, Settings, Star, Clock, Heart, Shield, Sparkles,
  ChevronLeft, UserPlus, Search, Users
} from "lucide-react";
import MirrorsContacts from "@/components/MirrorsContacts";
import CodesDirectConnect from "@/components/CodesDirectConnect";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = BACKEND_URL ? `${BACKEND_URL}/api` : '/api';

// Mood frequency mappings
const MOOD_FREQUENCIES = {
  CALM: { freq: 288, color: "#7BA0A3", label: "Calm" },
  ENERGIZED: { freq: 864, color: "#C41E3A", label: "Energized" },
  HAPPY: { freq: 324, color: "#FFD700", label: "Happy" },
  FOCUSED: { freq: 432, color: "#28880D", label: "Focused" },
  RELAXED: { freq: 256, color: "#97609A", label: "Relaxed" },
  CREATIVE: { freq: 528, color: "#E91E63", label: "Creative" },
  MEDITATIVE: { freq: 7.83, color: "#3F51B5", label: "Meditative" },
  BLISS: { freq: 999, color: "#00BCD4", label: "Bliss" }
};

const SAMPLE_CONTACTS = [];

export default function MirrorsSync() {
  // Account states
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupEmail, setSignupEmail] = useState("");
  const [signupName, setSignupName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  
  // Preferences state
  const [preferences, setPreferences] = useState({
    default_mood: "calm",
    preferred_carrier_hz: 432.0,
    headphone_mode: false,
    auto_play: false,
    session_duration_mins: 15,
    theme: "night",
    notifications_enabled: true,
    marketing_emails: true,
    weekly_digest: false
  });
  
  // Sync states
  const [username, setUsername] = useState("");
  const [isGenerated, setIsGenerated] = useState(false);
  const [connected, setConnected] = useState(false);
  const [currentMood, setCurrentMood] = useState("CALM");
  const [mirroredUsers, setMirroredUsers] = useState([]);
  const [copied, setCopied] = useState(false);
  const [e2eeEnabled, setE2eeEnabled] = useState(true);
  const [autoSync, setAutoSync] = useState(true);
  const [activeTab, setActiveTab] = useState("account");
  const [contactsGranted, setContactsGranted] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contactFilter, setContactFilter] = useState("all");
  const [contacts] = useState(SAMPLE_CONTACTS);

  // Deep-link: /mirrors?tab=sync
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab === "sync") setActiveTab("sync");
  }, []);

  
  // Stats
  const [userStats, setUserStats] = useState({
    total_sessions: 0,
    total_minutes: 0,
    favorite_frequencies: []
  });
  
  const wsRef = useRef(null);

  // Load user on mount
  useEffect(() => {
    const storedMirrorsId = localStorage.getItem("mirrors_id");
    if (storedMirrorsId) {
      loadUser(storedMirrorsId);
    }
    
    const storedUsername = localStorage.getItem("mirrors_username");
    if (storedUsername) {
      setUsername(storedUsername);
      setIsGenerated(true);
    }
  }, []);

  // Load user profile
  const loadUser = async (mirrorsId) => {
    try {
      const res = await axios.get(`${API}/users/${mirrorsId}`);
      setUser(res.data);
      setIsLoggedIn(true);
      setUsername(res.data.mirrors_id);
      setIsGenerated(true);
      if (res.data.preferences) {
        setPreferences(res.data.preferences);
      }
      setUserStats({
        total_sessions: res.data.total_sessions || 0,
        total_minutes: res.data.total_minutes || 0,
        favorite_frequencies: res.data.favorite_frequencies || []
      });
      localStorage.setItem("mirrors_id", mirrorsId);
      localStorage.setItem("mirrors_username", mirrorsId);
    } catch (err) {
      console.log("User not found, showing signup");
      localStorage.removeItem("mirrors_id");
    }
  };

  // Register new user
  const handleSignup = async () => {
    if (!signupEmail || !signupEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    
    setIsLoading(true);
    try {
      const res = await axios.post(`${API}/users/register`, {
        email: signupEmail,
        display_name: signupName || null
      });
      
      const mirrorsId = res.data.mirrors_id;
      localStorage.setItem("mirrors_id", mirrorsId);
      localStorage.setItem("mirrors_username", mirrorsId);
      
      await loadUser(mirrorsId);
      setShowSignupModal(false);
      toast.success(`Welcome to MIRRORS! Your ID: ${mirrorsId}`);
    } catch (err) {
      if (err.response?.data?.detail === "Email already registered") {
        // Try to find existing user
        try {
          const res = await axios.get(`${API}/users/email/${signupEmail}`);
          await loadUser(res.data.mirrors_id);
          setShowSignupModal(false);
          toast.success(`Welcome back! Your ID: ${res.data.mirrors_id}`);
        } catch {
          toast.error("Email already registered. Contact support to recover your account.");
        }
      } else {
        toast.error("Failed to create account. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Update preferences
  const updatePreferences = async (newPrefs) => {
    const updated = { ...preferences, ...newPrefs };
    setPreferences(updated);
    
    if (user?.mirrors_id) {
      try {
        await axios.put(`${API}/users/${user.mirrors_id}`, {
          preferences: updated
        });
        toast.success("Preferences saved!");
      } catch (err) {
        toast.error("Failed to save preferences");
      }
    }
  };

  // Generate random username for non-registered users
  const generateUsername = () => {
    const length = Math.floor(Math.random() * 8) + 6;
    const numCount = Math.floor(length * 0.5);
    const letterCount = length - numCount;
    
    const numbers = Array.from({ length: numCount }, () => Math.floor(Math.random() * 10)).join('');
    const letters = Array.from({ length: letterCount }, () => 
      String.fromCharCode(65 + Math.floor(Math.random() * 26))
    ).join('');
    
    const chars = (letters + numbers).split('');
    for (let i = chars.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [chars[i], chars[j]] = [chars[j], chars[i]];
    }
    
    const newUsername = chars.join('');
    setUsername(newUsername);
    setIsGenerated(true);
    localStorage.setItem("mirrors_username", newUsername);
    toast.success(`Temporary ID: ${newUsername}. Create an account to save your progress!`);
  };

  // Copy username
  const copyToClipboard = () => {
    navigator.clipboard.writeText(username);
    setCopied(true);
    toast.success("MIRRORS ID copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  // Logout
  const handleLogout = () => {
    localStorage.removeItem("mirrors_id");
    setUser(null);
    setIsLoggedIn(false);
    setUserStats({ total_sessions: 0, total_minutes: 0, favorite_frequencies: [] });
    toast.info("Logged out successfully");
  };

  const inviteLink = `mirrors://invite/${username || "guest"}`;
  const filteredContacts = contacts.filter((contact) => {
    const matchesSearch = contact.name.toLowerCase().includes(contactSearch.toLowerCase());
    const isMirrors = Boolean(contact.mirrorsId);
    const matchesFilter =
      contactFilter === "all" ||
      (contactFilter === "mirrors" && isMirrors) ||
      (contactFilter === "invite" && !isMirrors);
    return matchesSearch && matchesFilter;
  });

  const groupedContacts = filteredContacts.reduce((acc, contact) => {
    const letter = contact.name.charAt(0).toUpperCase();
    if (!acc[letter]) acc[letter] = [];
    acc[letter].push(contact);
    return acc;
  }, {});
  const contactLetters = Object.keys(groupedContacts).sort();

  const handleGrantContacts = () => {
    setContactsGranted(true);
    toast.success("Contacts permission granted");
  };

  const sendSmsInvite = (contact) => {
    if (!contact.phone) {
      toast.error("Add a phone number to send an invite");
      return;
    }
    const message = `Hey ${contact.name.split(" ")[0]}, join me on MIRRORS. ${inviteLink}`;
    const smsUrl = `sms:${encodeURIComponent(contact.phone)}?&body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
  };

  const inviteAll = () => {
    const invitees = contacts.filter((contact) => !contact.mirrorsId && contact.phone);
    if (invitees.length === 0) {
      toast.message("No invite-ready contacts found");
      return;
    }
    const numbers = invitees.map((contact) => contact.phone).join(",");
    const message = `Join me on MIRRORS. ${inviteLink}`;
    const smsUrl = `sms:${encodeURIComponent(numbers)}?&body=${encodeURIComponent(message)}`;
    window.location.href = smsUrl;
  };

  const connectToContact = (contact) => {
    if (!contact.mirrorsId) return;
    window.dispatchEvent(new CustomEvent("mirrors:quantum_open"));
    toast.message(`Connecting to ${contact.name}`);
  };

  // Subscribe email only (no account)
  const handleEmailSubscribe = async () => {
    if (!signupEmail || !signupEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    
    try {
      await axios.post(`${API}/subscribe`, {
        email: signupEmail,
        source: "sync_page"
      });
      toast.success("Subscribed! Check your email for updates.");
      setShowSignupModal(false);
    } catch (err) {
      toast.error("Failed to subscribe");
    }
  };

  return (
    <div className="min-h-screen p-4 md:p-6 pb-40 sm:pb-24" data-testid="mirrors-sync-page">
      <div className="mx-auto max-w-5xl space-y-6 pr-10 sm:pr-0">
        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-center gap-2 text-[#7BA0A3]" data-testid="mirrors-sync-back">
            <ChevronLeft className="h-4 w-4" />
            <span className="text-xs uppercase tracking-[0.2em]">Back</span>
          </div>
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_ed6a0deb-be55-415b-8702-764f1299165b/artifacts/enhrt4um_96%20Mirros%20Album%20Cover.PNG" 
              alt="GODMODE"
              className="w-12 h-12 rounded-full object-contain"
              style={{ filter: 'drop-shadow(0 0 8px rgba(40, 136, 13, 0.5))' }}
              data-testid="mirrors-sync-logo"
            />
            <div>
              <h1
                className="text-4xl font-bold text-[#28880D]"
                style={{ textShadow: '0 0 10px rgba(40, 136, 13, 0.5)' }}
                data-testid="mirrors-sync-title"
              >
                Sync
              </h1>
              <p className="text-xs text-[#7BA0A3]" data-testid="mirrors-sync-subtitle">
                MIRRORS by GODMODE SOUND
              </p>
            </div>
          </div>
          <div
            className="text-xs sm:text-sm text-[#ADB0AC] max-w-xl leading-relaxed"
            data-testid="mirrors-sync-tagline"
          >
            Clean, direct sync for voice + frequency sharing. Manage your MIRRORS ID and contacts below.
          </div>
        </div>

        {/* User Status Bar */}
        <Card className="bg-[#050505]/80 border-[#28880D]/30" data-testid="mirrors-sync-status">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {isLoggedIn && user ? (
                <>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-r from-[#28880D] to-[#97609A] flex items-center justify-center">
                      <User className="w-5 h-5 text-[#050505]" />
                    </div>
                    <div>
                      <div className="font-bold text-[#28880D]" data-testid="mirrors-sync-display-name">
                        {user.display_name || user.mirrors_id}
                      </div>
                      <div className="text-xs text-[#7BA0A3]" data-testid="mirrors-sync-user-id">
                        {user.mirrors_id}
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4">
                    <div className="text-left sm:text-center">
                      <div className="text-lg font-bold text-[#28880D]" data-testid="mirrors-sync-sessions-count">
                        {userStats.total_sessions}
                      </div>
                      <div className="text-xs text-[#ADB0AC]">Sessions</div>
                    </div>
                    <div className="text-left sm:text-center">
                      <div className="text-lg font-bold text-[#97609A]" data-testid="mirrors-sync-minutes-count">
                        {Math.round(userStats.total_minutes)}
                      </div>
                      <div className="text-xs text-[#ADB0AC]">Minutes</div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleLogout}
                      data-testid="mirrors-sync-logout-button"
                    >
                      Logout
                    </Button>
                  </div>
                </>
              ) : (
                <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4" data-testid="mirrors-sync-cta">
                  <div className="text-center sm:text-left">
                    <p className="text-[#E1E1E1]">
                      Create your <span className="text-[#28880D] font-bold">MIRRORS ID</span> for a personalized experience
                    </p>
                    <p className="text-xs text-[#ADB0AC]">Save preferences, track progress, unlock features.</p>
                  </div>
                  <Button 
                    onClick={() => setShowSignupModal(true)}
                    className="bg-gradient-to-r from-[#28880D] to-[#97609A] text-[#050505] font-bold w-full sm:w-auto"
                    data-testid="mirrors-sync-open-signup"
                  >
                    <Sparkles className="w-4 h-4 mr-2" />
                    Create MIRRORS ID
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList
            className="flex w-full gap-2 overflow-x-auto bg-black/70 rounded-full p-1"
            data-testid="mirrors-sync-tabs"
          >
            <TabsTrigger value="account" data-testid="mirrors-sync-tab-account" className="flex-1 min-w-[120px] rounded-full justify-center">
              <User className="w-4 h-4 mr-2" /> Account
            </TabsTrigger>
            <TabsTrigger value="contacts" data-testid="mirrors-sync-tab-contacts" className="flex-1 min-w-[120px] rounded-full justify-center">
              <Users className="w-4 h-4 mr-2" /> Contacts
            </TabsTrigger>
            <TabsTrigger value="preferences" data-testid="mirrors-sync-tab-preferences" className="flex-1 min-w-[140px] rounded-full justify-center">
              <Settings className="w-4 h-4 mr-2" /> Preferences
            </TabsTrigger>
            <TabsTrigger value="sync" data-testid="mirrors-sync-tab-live-sync" className="flex-1 min-w-[120px] rounded-full justify-center">
              <Radio className="w-4 h-4 mr-2" /> CODES
            </TabsTrigger>
          </TabsList>

        {/* ACCOUNT TAB */}
        <TabsContent value="account" className="space-y-6" data-testid="mirrors-sync-account-tab">
          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-5 sm:gap-6">
            <div className="space-y-6">
              {isLoggedIn && user ? (
                <Card className="bg-[#050505]/60 border-[#28880D]/30" data-testid="mirrors-profile-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#28880D]">
                      <Shield className="w-5 h-5" /> Your MIRRORS Profile
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#7BA0A3]">MIRRORS ID</Label>
                        <div className="flex items-center gap-2">
                          <Input
                            value={user.mirrors_id}
                            readOnly
                            className="bg-[#050505]/80"
                            data-testid="mirrors-profile-id"
                          />
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={copyToClipboard}
                            data-testid="mirrors-profile-copy-id"
                          >
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#7BA0A3]">Display Name</Label>
                        <Input 
                          value={user.display_name || ""} 
                          placeholder="Enter display name"
                          className="bg-[#050505]/80"
                          onChange={(e) => {
                            const newName = e.target.value;
                            setUser(prev => ({ ...prev, display_name: newName }));
                          }}
                          onBlur={async (e) => {
                            if (e.target.value && e.target.value !== user.display_name) {
                              await axios.put(`${API}/users/${user.mirrors_id}`, {
                                display_name: e.target.value
                              });
                              toast.success("Name updated!");
                            }
                          }}
                          data-testid="mirrors-profile-display-name"
                        />
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-[#7BA0A3]">Email</Label>
                        <Input
                          value={user.email}
                          readOnly
                          className="bg-[#050505]/80 opacity-70"
                          data-testid="mirrors-profile-email"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-[#7BA0A3]">Member Since</Label>
                        <Input 
                          value={new Date(user.created_at).toLocaleDateString()} 
                          readOnly 
                          className="bg-[#050505]/80 opacity-70"
                          data-testid="mirrors-profile-member-since"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="bg-[#050505]/60 border-[#28880D]/30" data-testid="mirrors-profile-empty">
                  <CardContent className="p-6 space-y-4">
                    <h2 className="text-xl font-bold text-[#28880D]">Create Your MIRRORS ID</h2>
                    <p className="text-[#ADB0AC]">
                      One ID gives you saved settings, contact sync, and quick PTT access.
                    </p>
                    <Button
                      onClick={() => setShowSignupModal(true)}
                      className="bg-gradient-to-r from-[#28880D] to-[#97609A] text-[#050505] font-bold w-full sm:w-auto"
                      data-testid="mirrors-profile-create-id"
                    >
                      <Sparkles className="w-5 h-5 mr-2" />
                      Create MIRRORS ID
                    </Button>
                  </CardContent>
                </Card>
              )}

              {isLoggedIn && userStats.favorite_frequencies.length > 0 && (
                <Card className="bg-[#050505]/60 border-[#28880D]/30" data-testid="mirrors-favorites-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#C41E3A]">
                      <Star className="w-5 h-5" /> Favorite Frequencies
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2" data-testid="mirrors-favorites-list">
                      {userStats.favorite_frequencies.map((freq, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-[#28880D]/20 text-[#28880D] rounded-full text-sm"
                          data-testid={`mirrors-favorite-${i}`}
                        >
                          {freq} Hz
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="space-y-6">
              {isLoggedIn ? (
                <Card className="bg-[#050505]/60 border-[#28880D]/30" data-testid="mirrors-stats-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-[#7BA0A3]">
                      <Activity className="w-5 h-5" /> Your Journey
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                      <div className="p-4 bg-[#050505]/50 rounded-lg" data-testid="mirrors-stat-sessions">
                        <Clock className="w-6 h-6 mx-auto mb-2 text-[#28880D]" />
                        <div className="text-2xl font-bold text-[#28880D]">{userStats.total_sessions}</div>
                        <div className="text-xs text-[#ADB0AC]">Total Sessions</div>
                      </div>
                      <div className="p-4 bg-[#050505]/50 rounded-lg" data-testid="mirrors-stat-minutes">
                        <Activity className="w-6 h-6 mx-auto mb-2 text-[#97609A]" />
                        <div className="text-2xl font-bold text-[#97609A]">{Math.round(userStats.total_minutes)}</div>
                        <div className="text-xs text-[#ADB0AC]">Minutes</div>
                      </div>
                      <div className="p-4 bg-[#050505]/50 rounded-lg" data-testid="mirrors-stat-favorites">
                        <Heart className="w-6 h-6 mx-auto mb-2 text-[#C41E3A]" />
                        <div className="text-2xl font-bold text-[#C41E3A]">{userStats.favorite_frequencies.length}</div>
                        <div className="text-xs text-[#ADB0AC]">Favorites</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : null}

              <MirrorsContacts mirrorsId={username} />
            </div>
          </div>
        </TabsContent>

        {/* CONTACTS TAB */}
        <TabsContent value="contacts" className="space-y-6" data-testid="mirrors-sync-contacts-tab">
          <Card className="bg-[#050505]/60 border-[#28880D]/30" data-testid="contacts-ios-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-[#28880D]">
                <Users className="w-5 h-5" /> My Contacts
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!contactsGranted ? (
                <div className="rounded-xl border border-[#28880D]/20 bg-black/40 p-4 space-y-3">
                  <div className="text-sm text-[#E1E1E1]">
                    Allow contacts access to invite friends instantly via SMS.
                  </div>
                  <Button
                    className="bg-[#28880D] text-black w-full sm:w-auto"
                    onClick={handleGrantContacts}
                    data-testid="contacts-grant-permission"
                  >
                    Grant Contacts Permission
                  </Button>
                </div>
              ) : (
                <>
                  <div className="relative" data-testid="contacts-search-bar">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7BA0A3]" />
                    <Input
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                      placeholder="Search contacts"
                      className="pl-10 rounded-full bg-black/70 border-[#28880D]/30 text-[#E1E1E1]"
                      data-testid="contacts-search-input"
                    />
                  </div>

                  <div className="text-xs text-[#7BA0A3]" data-testid="contacts-chirp-link">
                    MIRRORS chirp link: {inviteLink}
                  </div>

                  <div className="flex gap-2" data-testid="contacts-filter">
                    {[
                      { key: "all", label: "All" },
                      { key: "mirrors", label: "On MIRRORS" },
                      { key: "invite", label: "Invite" },
                    ].map((item) => (
                      <Button
                        key={item.key}
                        size="sm"
                        variant={contactFilter === item.key ? "default" : "outline"}
                        className={
                          contactFilter === item.key
                            ? "bg-[#28880D] text-black"
                            : "border-[#28880D]/40 text-[#28880D]"
                        }
                        onClick={() => setContactFilter(item.key)}
                        data-testid={`contacts-filter-${item.key}`}
                      >
                        {item.label}
                      </Button>
                    ))}
                  </div>

                  <div className="relative" data-testid="contacts-list-container">
                    <div className="max-h-[55vh] overflow-y-auto pr-6 space-y-4">
                      {contactLetters.length === 0 ? (
                        <div className="text-sm text-[#ADB0AC]" data-testid="ios-contacts-empty">
                          No contacts found.
                        </div>
                      ) : (
                        contactLetters.map((letter) => (
                          <div key={letter} className="space-y-2">
                            <div
                              className="sticky top-0 z-10 bg-[#050505] py-1 text-xs uppercase tracking-[0.4em] text-[#7BA0A3]"
                              data-testid={`ios-contacts-letter-${letter}`}
                            >
                              {letter}
                            </div>
                            {groupedContacts[letter].map((contact) => {
                              const isMirrors = Boolean(contact.mirrorsId);
                              const initials = contact.name
                                .split(" ")
                                .map((part) => part[0])
                                .join("")
                                .slice(0, 2)
                                .toUpperCase();

                              return (
                                <div
                                  key={contact.id}
                                  className="flex items-center justify-between gap-3 rounded-xl border border-[#28880D]/20 bg-black/50 p-3"
                                  data-testid={`ios-contacts-item-${contact.id}`}
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="relative">
                                      <div className="h-10 w-10 rounded-full bg-[#0d1f0d] flex items-center justify-center text-sm text-[#28880D]">
                                        {initials}
                                      </div>
                                      <span
                                        className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border border-[#050505] ${
                                          isMirrors ? "bg-[#28880D]" : "bg-[#ADB0AC]"
                                        }`}
                                        data-testid={`ios-contacts-status-${contact.id}`}
                                      />
                                    </div>
                                    <div>
                                      <div className="text-sm text-[#E1E1E1]" data-testid={`ios-contacts-name-${contact.id}`}>
                                        {contact.name}
                                      </div>
                                      <div className="text-[11px] text-[#7BA0A3]">
                                        {isMirrors ? contact.mirrorsId : contact.phone}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    {isMirrors ? (
                                      <Button
                                        size="sm"
                                        className="rounded-full bg-[#28880D] text-black"
                                        onClick={() => connectToContact(contact)}
                                        data-testid={`ios-contacts-connect-${contact.id}`}
                                      >
                                        Connect
                                      </Button>
                                    ) : (
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        className="rounded-full border-[#28880D]/40 text-[#28880D]"
                                        onClick={() => sendSmsInvite(contact)}
                                        data-testid={`ios-contacts-invite-${contact.id}`}
                                      >
                                        Invite
                                      </Button>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        ))
                      )}
                    </div>
                    {contactLetters.length > 0 && (
                      <div className="absolute right-0 top-2 flex flex-col items-center gap-1 text-[10px] text-[#7BA0A3]" data-testid="ios-contacts-index">
                        {contactLetters.map((letter) => (
                          <button
                            key={letter}
                            className="hover:text-[#28880D]"
                            onClick={() => {
                              const target = document.querySelector(`[data-testid='ios-contacts-letter-${letter}']`);
                              if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
                            }}
                            data-testid={`ios-contacts-index-${letter}`}
                          >
                            {letter}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="sticky bottom-3 flex justify-end" data-testid="contacts-invite-all-container">
                    <Button
                      className="rounded-full bg-[#C41E3A] text-white shadow-lg"
                      onClick={inviteAll}
                      data-testid="contacts-invite-all"
                    >
                      Invite All
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* PREFERENCES TAB */}
        <TabsContent value="preferences" className="space-y-6" data-testid="mirrors-sync-preferences-tab">
          <Card className="bg-[#050505]/60 border-[#28880D]/30" data-testid="preferences-core-card">
            <CardHeader>
              <CardTitle className="text-[#28880D]">Account Preferences</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#7BA0A3]">Default Mood</Label>
                  <Select 
                    value={preferences.default_mood} 
                    onValueChange={(v) => updatePreferences({ default_mood: v })}
                  >
                    <SelectTrigger data-testid="preferences-default-mood">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="calm" data-testid="preferences-default-mood-calm">calm</SelectItem>
                      <SelectItem value="focus" data-testid="preferences-default-mood-focus">🎯 Focus</SelectItem>
                      <SelectItem value="sleep" data-testid="preferences-default-mood-sleep">😴 Sleep</SelectItem>
                      <SelectItem value="energize" data-testid="preferences-default-mood-energize">⚡ Energize</SelectItem>
                      <SelectItem value="uplift" data-testid="preferences-default-mood-uplift">✨ Uplift</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="text-[#7BA0A3]">Session Duration</Label>
                  <Select 
                    value={String(preferences.session_duration_mins)} 
                    onValueChange={(v) => updatePreferences({ session_duration_mins: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="preferences-session-duration">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5" data-testid="preferences-session-5">5 minutes</SelectItem>
                      <SelectItem value="10" data-testid="preferences-session-10">10 minutes</SelectItem>
                      <SelectItem value="15" data-testid="preferences-session-15">15 minutes</SelectItem>
                      <SelectItem value="20" data-testid="preferences-session-20">20 minutes</SelectItem>
                      <SelectItem value="30" data-testid="preferences-session-30">30 minutes</SelectItem>
                      <SelectItem value="45" data-testid="preferences-session-45">45 minutes</SelectItem>
                      <SelectItem value="60" data-testid="preferences-session-60">60 minutes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-[#7BA0A3]">Preferred Carrier (Hz)</Label>
                  <Select 
                    value={String(preferences.preferred_carrier_hz)} 
                    onValueChange={(v) => updatePreferences({ preferred_carrier_hz: parseFloat(v) })}
                  >
                    <SelectTrigger data-testid="preferences-carrier">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="216" data-testid="preferences-carrier-216">216 Hz - Delta</SelectItem>
                      <SelectItem value="288" data-testid="preferences-carrier-288">288 Hz - Theta</SelectItem>
                      <SelectItem value="324" data-testid="preferences-carrier-324">324 Hz - Alpha Low</SelectItem>
                      <SelectItem value="432" data-testid="preferences-carrier-432">432 Hz - Alpha</SelectItem>
                      <SelectItem value="864" data-testid="preferences-carrier-864">864 Hz - Beta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <Separator className="border-[#28880D]/30" />

              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-[#E1E1E1]">Headphone Mode</Label>
                    <p className="text-xs text-[#ADB0AC]">Binaural beats enabled</p>
                  </div>
                  <Switch 
                    checked={preferences.headphone_mode}
                    onCheckedChange={(v) => updatePreferences({ headphone_mode: v })}
                    data-testid="preferences-headphone-mode"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-[#E1E1E1]">Notifications</Label>
                    <p className="text-xs text-[#ADB0AC]">Important sync alerts</p>
                  </div>
                  <Switch 
                    checked={preferences.notifications_enabled}
                    onCheckedChange={(v) => updatePreferences({ notifications_enabled: v })}
                    data-testid="preferences-notifications"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-[#E1E1E1]">Email Updates</Label>
                    <p className="text-xs text-[#ADB0AC]">New frequencies + product updates</p>
                  </div>
                  <Switch 
                    checked={preferences.marketing_emails}
                    onCheckedChange={(v) => updatePreferences({ marketing_emails: v })}
                    data-testid="preferences-email-updates"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CODES DIRECT CONNECT TAB */}
        <TabsContent value="sync" className="space-y-6" data-testid="mirrors-sync-live-tab">
          <Card className="bg-[#050505]/60 border-[#FF0000]/20" data-testid="codes-dc-card">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-[#FF0000] text-lg font-mono tracking-wider">
                <Radio className="w-5 h-5" /> CODES
              </CardTitle>
              <p className="text-[10px] font-mono text-[#666] tracking-[0.15em] uppercase">
                Direct Connect — CODES by GODMODE LLC
              </p>
            </CardHeader>
            <CardContent>
              {username ? (
                <CodesDirectConnect />
              ) : (
                <div className="space-y-4 py-4">
                  <p className="text-sm text-[#999] font-mono">
                    Create a MIRRORS ID to enable CODES Direct Connect.
                  </p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      className="border-[#FF0000]/40 text-[#FF0000] hover:bg-[#1a0000] font-mono"
                      onClick={generateUsername}
                      data-testid="codes-generate-id"
                    >
                      GENERATE TEMP ID
                    </Button>
                    <Button
                      className="bg-[#FF0000] text-black font-mono"
                      onClick={() => setShowSignupModal(true)}
                      data-testid="codes-create-id"
                    >
                      Create MIRRORS ID
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Signup Modal */}
      <Dialog open={showSignupModal} onOpenChange={setShowSignupModal}>
        <DialogContent
          className="bg-[#050505] border-[#28880D]/50 max-w-md"
          data-testid="mirrors-sync-signup-modal"
        >
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_ed6a0deb-be55-415b-8702-764f1299165b/artifacts/enhrt4um_96%20Mirros%20Album%20Cover.PNG" 
                alt="GODMODE"
                className="w-12 h-12 rounded-full"
                style={{ filter: 'drop-shadow(0 0 8px rgba(40, 136, 13, 0.5))' }}
              />
              <div>
                <DialogTitle className="text-[#28880D]">Create Your MIRRORS ID</DialogTitle>
                <DialogDescription className="text-[#7BA0A3]">
                  Join the frequency experience
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-[#7BA0A3]">Email Address *</Label>
              <Input
                type="email"
                placeholder="your@email.com"
                value={signupEmail}
                onChange={(e) => setSignupEmail(e.target.value)}
                className="bg-[#050505]/80"
                data-testid="mirrors-sync-signup-email"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-[#7BA0A3]">Display Name (optional)</Label>
              <Input
                placeholder="How should we call you?"
                value={signupName}
                onChange={(e) => setSignupName(e.target.value)}
                className="bg-[#050505]/80"
                data-testid="mirrors-sync-signup-name"
              />
            </div>
            
            <div className="text-xs text-[#ADB0AC] p-3 bg-[#050505]/50 rounded">
              <p>✓ Your unique MIRRORS ID will be auto-generated</p>
              <p>✓ Save your preferences and track progress</p>
              <p>✓ Get early access to new frequencies</p>
            </div>
            
            <Button 
              onClick={handleSignup}
              disabled={isLoading}
              className="w-full bg-gradient-to-r from-[#28880D] to-[#97609A] text-[#050505] font-bold"
              data-testid="mirrors-sync-signup-submit"
            >
              {isLoading ? "Creating..." : "Create MIRRORS ID"}
            </Button>
            
            <div className="text-center">
              <span className="text-xs text-[#ADB0AC]">Just want updates? </span>
              <button 
                onClick={handleEmailSubscribe}
                className="text-xs text-[#28880D] hover:underline"
                data-testid="mirrors-sync-subscribe-only"
              >
                Subscribe to newsletter only
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  </div>
  );
}
